import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  CurrencyRupeeOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  PendingActionsOutlined,
  PrintOutlined,
  ReceiptLongOutlined,
  SearchOutlined,
  TaskAltOutlined,
  VisibilityOutlined,
  ExpandLess as ExpandLessIcon,
  AccountBalanceWalletOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import invoiceApi from '../../api/invoiceApi';
import backgroundPattern from '../../assets/pasbookpng.png';
import './Invoice.css';

const FILTERS = ['All', 'Draft', 'Sent', 'Paid', 'Overdue'];

const money = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const date = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const recordsFromResponse = (response) => {
  const root = response?.data;
  const data = root?.result ?? root?.data ?? root;
  const records = data?.content ?? data?.items ?? data?.records ?? data ?? [];
  return Array.isArray(records) ? records : [];
};

const statusOf = (invoice) => {
  if (invoice.status === 'Paid') return 'Paid';
  if (invoice.dueDate && invoice.status !== 'Draft' && invoice.dueDate < new Date().toISOString().slice(0, 10)) return 'Overdue';
  return invoice.status || 'Draft';
};

const Status = ({ invoice }) => {
  const status = statusOf(invoice);
  return <Box className={`invoice-status invoice-status-${status.toLowerCase()}`}><span />{status}</Box>;
};

const ViewInvoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await invoiceApi.getAllInvoices();
      setInvoices(recordsFromResponse(response));
    } catch (error) {
      console.error('Failed to load invoices:', error);
      setInvoices([]);
      toast.error(error?.response?.data?.message || 'Unable to load invoices.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const value = invoices.reduce((sum, item) => sum + Number(item.total ?? item.totalAmount ?? 0), 0);
    const paid = invoices.reduce((sum, item) => sum + Number(item.paidAmount ?? 0), 0);
    const outstanding = invoices.reduce((sum, item) => sum + Number(item.outstandingAmount ?? item.total ?? item.totalAmount ?? 0), 0);
    return { count: invoices.length, value, paid, outstanding: Math.max(outstanding, 0) };
  }, [invoices]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return invoices.filter((invoice) => {
      const matchesText = !term ||
        String(invoice.invoiceNumber || invoice.number || invoice.orderNumber || '').toLowerCase().includes(term) ||
        String(invoice.customerName || invoice.customer?.name || '').toLowerCase().includes(term);
      return matchesText && (filter === 'All' || statusOf(invoice) === filter);
    });
  }, [invoices, search, filter]);

  const deleteInvoice = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await invoiceApi.deleteInvoice(deleteTarget.id);
      setInvoices((current) => current.filter((item) => item.id !== deleteTarget.id));
      setDeleteTarget(null);
      setSelected(null);
      toast.success('Invoice deleted successfully.');
    } catch (error) {
      console.error('Invoice delete failed:', error);
      toast.error(error?.response?.data?.message || 'Unable to delete invoice.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const response = await invoiceApi.downloadInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Tax_Invoice_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handlePreviewPdf = async (invoiceId) => {
    try {
      const response = await invoiceApi.previewInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('PDF preview failed:', error);
      toast.error('Failed to preview PDF');
    }
  };

  return (
    <Box className="invoice-page invoice-list-page">
      <div className="invoice-background-watermark" style={{ backgroundImage: `url(${backgroundPattern})` }} />
      <Box className="invoice-shell">
        <Box className="invoice-toolbar">
          <Box className="invoice-toolbar-left invoice-header-title-group">
            <Box className="invoice-page-icon"><ReceiptLongOutlined /></Box>
            <Box className="invoice-page-title-wrap">
              <Typography className="invoice-page-title">Invoices</Typography>
              <Typography className="invoice-page-subtitle">Manage, track and review customer invoices.</Typography>
            </Box>
          </Box>
          <Button className="invoice-header-create-btn" startIcon={<Add />} onClick={() => navigate('/dashboard/invoices/create')}>
            Create invoice
          </Button>
        </Box>

        <Box className="invoice-list-summary">
          <Card className="invoice-summary-card invoice-summary-card-total">
            <Box className="invoice-summary-icon"><ReceiptLongOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Total invoices</span>
              <strong>{stats.count}</strong>
              <small>All invoices</small>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-value">
            <Box className="invoice-summary-icon"><CurrencyRupeeOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Total value</span>
              <strong>{money(stats.value)}</strong>
              <small>Invoice amount</small>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-paid">
            <Box className="invoice-summary-icon"><TaskAltOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Paid</span>
              <strong>{money(stats.paid)}</strong>
              <small>Collected amount</small>
            </Box>
          </Card>
          <Card className="invoice-summary-card invoice-summary-card-outstanding">
            <Box className="invoice-summary-icon"><PendingActionsOutlined /></Box>
            <Box className="invoice-summary-body">
              <span className="invoice-summary-label">Outstanding</span>
              <strong>{money(stats.outstanding)}</strong>
              <small>Pending collection</small>
            </Box>
          </Card>
        </Box>

        {!filtersOpen ? (
          <button
            type="button"
            className="invoice-show-filters-btn"
            onClick={() => setFiltersOpen(true)}
          >
            <SearchOutlined className="invoice-show-filters-icon" />
            <span>Show Filters</span>
          </button>
        ) : (
          <Card className="invoice-filter-card">
            <CardContent>
              <Box className="invoice-filter-head">
                <Box className="invoice-filter-head-left">
                  <button
                    type="button"
                    className="invoice-filter-title-btn"
                    onClick={() => setFiltersOpen(false)}
                    aria-label="Hide filters"
                    title="Hide filters"
                  >
                    <Box className="invoice-filter-icon"><SearchOutlined /></Box>
                    <Typography className="invoice-filter-title">Filters</Typography>
                    <ExpandLessIcon className="invoice-filter-collapse-icon" />
                  </button>
                </Box>
                <Button className="invoice-filter-clear" onClick={() => { setSearch(''); setFilter('All'); }} disabled={!search && filter === 'All'}>Clear</Button>
              </Box>
              <Stack className="invoice-filter-controls" direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField className="invoice-field invoice-search-field" placeholder="Search invoice number or customer" value={search}
                  onChange={(e) => setSearch(e.target.value)} InputProps={{ startAdornment: <SearchOutlined sx={{ color: '#94a3b8', mr: 1 }} /> }} />
                <TextField select className="invoice-field invoice-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)}>
                  {FILTERS.map((item) => <MenuItem key={item} value={item}>{item === 'All' ? 'All statuses' : item}</MenuItem>)}
                </TextField>
                <Typography className="invoice-results-count">{filtered.length} invoice{filtered.length === 1 ? '' : 's'}</Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Card className="invoice-list-card">
          {loading ? (
            <Box className="invoice-loading"><CircularProgress size={28} /><Typography>Loading invoices…</Typography></Box>
          ) : filtered.length === 0 ? (
            <Box className="invoice-empty">
              <Box className="invoice-empty-icon"><ReceiptLongOutlined /></Box>
              <Typography>No invoices found</Typography>
              <span>{invoices.length ? 'Try changing your search or status filter.' : 'Create your first invoice to see it here.'}</span>
              {!invoices.length && <Button className="invoice-primary-btn" startIcon={<Add />} onClick={() => navigate('/dashboard/invoices/create')}>Create invoice</Button>}
            </Box>
          ) : (
            <Box className="invoice-table-container">
              <Table className="invoice-professional-table invoice-responsive-list-table">
                <TableHead><TableRow>
                  <TableCell>Invoice #</TableCell><TableCell>Customer</TableCell><TableCell>Invoice date</TableCell><TableCell>Due date</TableCell>
                  <TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell align="center">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>{filtered.map((invoice) => (
                  <TableRow key={invoice.id} hover className="invoice-row-card">
                    <TableCell data-label="Invoice #"><button type="button" className="invoice-number-link" onClick={() => setSelected(invoice)}>{invoice.invoiceNumber || invoice.number || invoice.orderNumber || '-'}</button></TableCell>
                    <TableCell data-label="Customer"><Box><Typography className="invoice-customer-name">{invoice.customerName || invoice.customer?.name || '-'}</Typography>{(invoice.customerPhone || invoice.customer?.contactNumber) && <Typography className="invoice-customer-phone">{invoice.customerPhone || invoice.customer.contactNumber}</Typography>}</Box></TableCell>
                    <TableCell data-label="Invoice date">{date(invoice.invoiceDate || invoice.date || invoice.startOn)}</TableCell><TableCell data-label="Due date">{date(invoice.dueDate || invoice.endsOn)}</TableCell>
                    <TableCell align="right" data-label="Amount"><Typography className="invoice-amount">{money(invoice.total ?? invoice.totalAmount)}</Typography></TableCell>
                    <TableCell data-label="Status"><Status invoice={invoice} /></TableCell>
                    <TableCell align="center" className="invoice-actions-cell"><Box className="invoice-row-actions">
                      <IconButton className="invoice-row-action invoice-row-view" onClick={() => setSelected(invoice)} title="View invoice"><VisibilityOutlined /></IconButton>
                      <IconButton className="invoice-row-action invoice-row-edit" onClick={() => navigate('/dashboard/invoices/create', { state: { invoice } })} title="Edit invoice"><EditOutlined /></IconButton>
                      <IconButton className="invoice-row-action" onClick={() => navigate('/dashboard/payments/new', { state: { invoiceData: { id: invoice.id, invoiceNumber: invoice.invoiceNumber || invoice.orderNumber, customerName: invoice.customerName || invoice.customer?.name, customerId: invoice.customerId || invoice.customer?.id, total: invoice.total } } })} title="Record Payment" sx={{ color: '#16a34a' }}><AccountBalanceWalletOutlined /></IconButton>
                      <IconButton className="invoice-row-action" onClick={() => handlePreviewPdf(invoice.id)} title="Preview PDF"><PictureAsPdfOutlined /></IconButton>
                      <IconButton className="invoice-row-action" onClick={() => handleDownloadPdf(invoice.id)} title="Download PDF"><FileDownloadOutlined /></IconButton>
                      <IconButton className="invoice-row-action invoice-row-delete" onClick={() => setDeleteTarget(invoice)} title="Delete invoice"><DeleteOutlineOutlined /></IconButton>
                    </Box></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </Box>
          )}
        </Card>
      </Box>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md" className="invoice-view-dialog">
        <DialogTitle className="invoice-dialog-header"><Box><Typography className="invoice-dialog-number">{selected?.invoiceNumber || selected?.number || selected?.orderNumber}</Typography><Typography className="invoice-dialog-caption">Customer invoice</Typography></Box>{selected && <Status invoice={selected} />}</DialogTitle>
        <DialogContent className="invoice-dialog-content">
          {selected && <>
            <Box className="invoice-preview-header">
              <Box><Typography className="invoice-preview-label">Bill to</Typography><Typography className="invoice-preview-customer">{selected.customerName || selected.customer?.name || '-'}</Typography><Typography>{selected.customerPhone || selected.customer?.contactNumber || ''}</Typography><Typography>{selected.customerEmail || selected.customer?.email || ''}</Typography><Typography>{selected.customerAddress || selected.customer?.address || ''}</Typography></Box>
              <Box className="invoice-preview-meta"><Box><span>Invoice date</span><strong>{date(selected.invoiceDate || selected.date || selected.startOn)}</strong></Box><Box><span>Due date</span><strong>{date(selected.dueDate || selected.endsOn)}</strong></Box></Box>
            </Box>
            <Table className="invoice-preview-table"><TableHead><TableRow><TableCell>Description</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Tax</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
              <TableBody>{(selected.items || []).map((item, index) => { const base = Number(item.quantity || 0) * Number(item.rate || 0); const amount = item.amount ?? base + (base * Number(item.tax || 0)) / 100; return <TableRow key={item.id || index} className="invoice-row-card"><TableCell data-label="Description">{item.description || item.name || '-'}</TableCell><TableCell align="right" data-label="Qty">{item.quantity ?? item.qty ?? 0}</TableCell><TableCell align="right" data-label="Rate">{money(item.rate)}</TableCell><TableCell align="right" data-label="Tax">{item.tax || 0}%</TableCell><TableCell align="right" data-label="Amount">{money(amount)}</TableCell></TableRow>; })}</TableBody>
            </Table>
            <Box className="invoice-preview-bottom"><Box>{selected.notes && <><Typography className="invoice-preview-label">Notes</Typography><Typography className="invoice-preview-note">{selected.notes}</Typography></>}</Box><Box className="invoice-preview-totals"><Stack><span>Subtotal</span><strong>{money(selected.subTotal ?? selected.subtotal)}</strong></Stack>{Number(selected.discountAmount) > 0 && <Stack><span>Discount</span><strong>-{money(selected.discountAmount)}</strong></Stack>}<Stack><span>Tax</span><strong>{money(selected.taxAmount ?? selected.tax)}</strong></Stack><Divider /><Stack className="invoice-preview-total"><span>Grand total</span><strong>{money(selected.total ?? selected.totalAmount)}</strong></Stack></Box></Box>
          </>}
        </DialogContent>
        <DialogActions className="invoice-dialog-actions"><Button className="invoice-secondary-btn" startIcon={<PrintOutlined />} onClick={() => window.print()}>Print</Button><Button className="invoice-secondary-btn" startIcon={<PictureAsPdfOutlined />} onClick={() => handlePreviewPdf(selected.id)}>Preview PDF</Button><Button className="invoice-secondary-btn" startIcon={<FileDownloadOutlined />} onClick={() => handleDownloadPdf(selected.id)}>Download PDF</Button><Stack direction="row" spacing={1}><Button className="invoice-secondary-btn" onClick={() => setSelected(null)}>Close</Button><Button className="invoice-primary-btn" onClick={() => { const invoice = selected; setSelected(null); navigate('/dashboard/invoices/create', { state: { invoice } }); }}>Edit invoice</Button></Stack></DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth className="invoice-confirm-dialog">
        <DialogTitle className="invoice-confirm-title">Delete invoice?</DialogTitle>
        <DialogContent className="invoice-confirm-body"><Typography>{deleteTarget?.invoiceNumber || deleteTarget?.number || deleteTarget?.orderNumber} will be permanently removed.</Typography></DialogContent>
        <DialogActions className="invoice-confirm-actions"><Button className="invoice-secondary-btn" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button><Button className="invoice-danger-btn" onClick={deleteInvoice} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewInvoices;